import React, { useEffect, useState } from 'react';
import { Plus, X, Edit3, Trash2, Star, ExternalLink, Save } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const emptyProject = {
  title: '',
  titleEn: '',
  description: '',
  descriptionEn: '',
  category: '',
  image: '',
  technologies: '',
  client: '',
  link: '',
  featured: false,
  order: 0
};

const PortfolioAdmin = ({ featuredOnly = false, onChanged }) => {
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [saving, setSaving] = useState(false);

  const loadProjects = async () => {
    try {
      const { data } = await api.get('/portfolio');
      setProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('تعذر تحميل المشاريع');
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyProject);
    setShowForm(true);
  };

  const openEdit = (project) => {
    setEditingId(project._id);
    setForm({
      title: project.title || '',
      titleEn: project.titleEn || '',
      description: project.description || '',
      descriptionEn: project.descriptionEn || '',
      category: project.category || '',
      image: project.image || '',
      technologies: Array.isArray(project.technologies) ? project.technologies.join(', ') : '',
      client: project.client || '',
      link: project.link || '',
      featured: Boolean(project.featured),
      order: Number(project.order || 0)
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditingId(null);
    setForm(emptyProject);
  };

  const saveProject = async (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.description.trim() || !form.category.trim()) {
      toast.error('اسم المشروع والوصف والتصنيف مطلوبة');
      return;
    }

    const payload = {
      ...form,
      order: Number(form.order || 0),
      technologies: form.technologies
        .split(',')
        .map(x => x.trim())
        .filter(Boolean)
    };

    try {
      setSaving(true);
      if (editingId) {
        await api.put(`/portfolio/${editingId}`, payload);
        toast.success('تم تعديل المشروع');
      } else {
        await api.post('/portfolio', payload);
        toast.success('تم إضافة المشروع');
      }

      closeForm();
      await loadProjects();
      if (onChanged) onChanged();
    } catch (error) {
      const message = error?.response?.data?.message || 'تعذر حفظ المشروع';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async (project) => {
    if (!window.confirm(`هل تريد حذف مشروع "${project.title}"؟`)) return;

    try {
      await api.delete(`/portfolio/${project._id}`);
      toast.success('تم حذف المشروع');
      await loadProjects();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error('تعذر حذف المشروع');
    }
  };

  const visibleProjects = projects.filter(project => !featuredOnly || project.featured);

  return (
    <div>
      <div className="flex items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">المشاريع</h1>
          <p className="text-gray-500 text-sm mt-1">
            إضافة وتعديل وحذف المشاريع، والتغييرات تظهر في الموقع الخارجي.
          </p>
          {featuredOnly && (
            <p className="text-sm text-violet-400 mt-1">عرض المشاريع المميزة فقط</p>
          )}
        </div>

        <button
          type="button"
          onClick={openAdd}
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Plus size={16} />
          إضافة مشروع
        </button>
      </div>

      {visibleProjects.length === 0 ? (
        <div className="glass p-10 text-center text-gray-400">
          لا توجد مشاريع حالياً.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleProjects.map(project => (
            <div key={project._id} className="glass p-6">
              <div className="h-36 rounded-xl overflow-hidden bg-gradient-to-br from-orvix-primary/20 to-orvix-green/20 flex items-center justify-center mb-4">
                {project.image ? (
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl font-bold text-orvix-primary">
                    {(project.title || '?')[0]}
                  </span>
                )}
              </div>

              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-lg font-bold text-white">{project.title}</h3>
                {project.featured && <Star size={17} className="text-yellow-400 fill-yellow-400" />}
              </div>

              <p className="text-orvix-green text-xs mb-2">{project.category}</p>
              <p className="text-gray-400 text-sm line-clamp-3 mb-4">
                {project.description}
              </p>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(project)}
                  className="flex-1 py-2 rounded-lg bg-orvix-primary/20 text-orvix-primary text-sm hover:bg-orvix-primary/30 transition-colors flex items-center justify-center gap-1"
                >
                  <Edit3 size={14} />
                  تعديل
                </button>

                <button
                  type="button"
                  onClick={() => deleteProject(project)}
                  className="flex-1 py-2 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 transition-colors flex items-center justify-center gap-1"
                >
                  <Trash2 size={14} />
                  حذف
                </button>

                {project.link && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10"
                    title="فتح الرابط"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-2xl bg-[#101d32] border border-white/10 shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between p-5 bg-[#101d32] border-b border-white/10">
              <div>
                <h2 className="text-xl font-bold text-white">
                  {editingId ? 'تعديل المشروع' : 'إضافة مشروع جديد'}
                </h2>
                <p className="text-gray-500 text-xs mt-1">الحقول الأساسية: الاسم، الوصف، التصنيف.</p>
              </div>
              <button type="button" onClick={closeForm} className="p-2 rounded-lg hover:bg-white/10 text-gray-400">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={saveProject} className="p-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="اسم المشروع بالعربي *">
                  <input
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    className="admin-input"
                    required
                  />
                </Field>

                <Field label="Project Name (English)">
                  <input
                    value={form.titleEn}
                    onChange={e => setForm({ ...form, titleEn: e.target.value })}
                    className="admin-input"
                  />
                </Field>

                <Field label="التصنيف *">
                  <input
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    className="admin-input"
                    placeholder="مثال: طبي، مقاولات، محاسبة"
                    required
                  />
                </Field>

                <Field label="العميل">
                  <input
                    value={form.client}
                    onChange={e => setForm({ ...form, client: e.target.value })}
                    className="admin-input"
                  />
                </Field>

                <Field label="رابط المشروع">
                  <input
                    value={form.link}
                    onChange={e => setForm({ ...form, link: e.target.value })}
                    className="admin-input"
                    placeholder="https://..."
                  />
                </Field>

                <Field label="رابط الصورة">
                  <input
                    value={form.image}
                    onChange={e => setForm({ ...form, image: e.target.value })}
                    className="admin-input"
                    placeholder="https://..."
                  />
                </Field>

                <Field label="الترتيب">
                  <input
                    type="number"
                    value={form.order}
                    onChange={e => setForm({ ...form, order: e.target.value })}
                    className="admin-input"
                  />
                </Field>

                <Field label="التقنيات">
                  <input
                    value={form.technologies}
                    onChange={e => setForm({ ...form, technologies: e.target.value })}
                    className="admin-input"
                    placeholder="React, .NET, MongoDB"
                  />
                </Field>
              </div>

              <Field label="الوصف بالعربي *">
                <textarea
                  rows="4"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="admin-input resize-y"
                  required
                />
              </Field>

              <Field label="Description (English)">
                <textarea
                  rows="3"
                  value={form.descriptionEn}
                  onChange={e => setForm({ ...form, descriptionEn: e.target.value })}
                  className="admin-input resize-y"
                />
              </Field>

              <label className="flex items-center gap-3 text-gray-300">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={e => setForm({ ...form, featured: e.target.checked })}
                  className="w-4 h-4"
                />
                مشروع مميز
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-5 py-2.5 rounded-xl border border-white/10 text-gray-300 hover:bg-white/5"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                >
                  <Save size={16} />
                  {saving ? 'جاري الحفظ...' : 'حفظ المشروع'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .admin-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border-radius: 0.75rem;
          background: rgba(255,255,255,.05);
          border: 1px solid rgba(255,255,255,.1);
          color: white;
          outline: none;
        }
        .admin-input:focus {
          border-color: rgba(45, 212, 191, .65);
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm text-gray-300 mb-2">{label}</span>
    {children}
  </label>
);

export default PortfolioAdmin;
